/** Журнал действий модераторов: фильтры по действию и исполнителю. */

import { useMemo, useState, type ReactNode } from 'react';
import type { AuditEntry } from '@ak/shared';

import { formatDateTime } from '../lib/format.ts';
import { useResource } from '../lib/useResource.ts';
import { Badge, EmptyState, ErrorState, PageHeader, Panel, Select, Skeleton } from '../components/ui.tsx';

const ACTION_LABEL: Record<string, string> = {
  'event.create': 'Создан набор',
  'event.update_time': 'Изменено время',
  'event.close': 'Набор закрыт',
  'event.reopen': 'Набор открыт',
  'event.cancel': 'Набор отменён',
  'event.result': 'Отмечен итог',
  'event.territory': 'Отмечена тера',
  'event.delete': 'Набор удалён',
  'event.notify': 'Отправлено уведомление',
  'member.join': 'Записался в набор',
  'member.leave': 'Вышел из набора',
  'member.promote': 'Перенесён в основу',
  'member.demote': 'Перенесён в замену',
  'member.remove': 'Убран из набора',
  'profile.upsert': 'Заполнена анкета',
  'profile.edit': 'Изменена анкета',
  'profile.delete': 'Удалена анкета',
  'family.submit': 'Подана заявка',
  'family.accept': 'Заявка принята',
  'family.invite_call': 'Приглашение на обзвон',
  'family.reject': 'Заявка отклонена',
  'rollback.request': 'Запрошен откат',
  'rollback.submit': 'Сдан откат',
  'rollback.channel': 'Заведён канал откатов',
  'rollback.channel_failed': 'Канал откатов не создался',
  'settings.update': 'Изменены настройки',
  'combat.family': 'Определена организация',
  'role.aptx_granted': 'Выдана роль aptx',
  'role.cleared': 'Сняты роли',
  'role.tier_changed': 'Изменён тир',
  'event.voice_check': 'Проверен сбор в голосовом',
};

/**
 * Действия, сгруппированные для выпадающего списка.
 *
 * Двадцать с лишним строк подряд читать невозможно, а по разделам
 * нужное находится сразу.
 */
const ACTION_GROUPS: { label: string; actions: string[] }[] = [
  {
    label: 'Наборы',
    actions: [
      'event.create',
      'event.update_time',
      'event.close',
      'event.reopen',
      'event.cancel',
      'event.result',
      'event.territory',
      'event.delete',
      'event.notify',
      'event.voice_check',
    ],
  },
  {
    label: 'Состав',
    actions: ['member.join', 'member.leave', 'member.promote', 'member.demote', 'member.remove'],
  },
  { label: 'Анкеты', actions: ['profile.upsert', 'profile.edit', 'profile.delete'] },
  { label: 'Заявки', actions: ['family.submit', 'family.accept', 'family.invite_call', 'family.reject'] },
  {
    label: 'Откаты',
    actions: ['rollback.request', 'rollback.submit', 'rollback.channel', 'rollback.channel_failed'],
  },
  { label: 'Прочее', actions: ['settings.update', 'combat.family', 'role.aptx_granted', 'role.cleared', 'role.tier_changed'] },
];

const SOURCE_LABEL: Record<string, string> = {
  discord: 'Discord',
  web: 'Админка',
  system: 'Система',
};

interface AuditResponse {
  entries: AuditEntry[];
  total: number;
  /** Имена по идентификаторам: журнал хранит только идентификаторы. */
  names: Record<string, string>;
  actors: { actorId: string; count: number; name: string | null }[];
}

export function AuditPage({ timezone }: { timezone: string }): ReactNode {
  const [action, setAction] = useState('');
  const [actor, setActor] = useState('');

  // Фильтры уходят на сервер: в журнале тысячи записей, и отбирать их
  // после выдачи означало бы показывать неполную картину.
  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '200' });
    if (action !== '') {
      params.set('action', action);
    }

    if (actor !== '') {
      params.set('actor', actor);
    }

    return `/api/audit?${params.toString()}`;
  }, [action, actor]);

  const { data, loading, error, reload } = useResource<AuditResponse>(path);

  if (error !== null) {
    return <ErrorState message={error} onRetry={reload} />;
  }

  const nameOf = (userId: string): string | null => data?.names[userId] ?? null;
  const filtered = action !== '' || actor !== '';

  return (
    <>
      <PageHeader
        title="Журнал действий"
        description={
          data === null
            ? undefined
            : filtered
              ? `Подходит записей: ${String(data.total)}`
              : `Всего записей: ${String(data.total)}`
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="mb-1.5 block text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
            Действие
          </span>
          <Select
            className="min-w-56"
            value={action}
            onChange={(event) => {
              setAction(event.target.value);
            }}
          >
            <option value="">Любое действие</option>
            {ACTION_GROUPS.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.actions.map((value) => (
                  <option key={value} value={value}>
                    {ACTION_LABEL[value] ?? value}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
        </label>

        <label className="block">
          <span className="mb-1.5 block text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
            Исполнитель
          </span>
          <Select
            className="min-w-56"
            value={actor}
            onChange={(event) => {
              setActor(event.target.value);
            }}
          >
            <option value="">Кто угодно</option>
            {(data?.actors ?? []).map((item) => (
              <option key={item.actorId} value={item.actorId}>
                {item.name ?? item.actorId} ({item.count})
              </option>
            ))}
          </Select>
        </label>

        {filtered && (
          <button
            className="h-10 text-sm underline"
            style={{ color: 'var(--accent)' }}
            onClick={() => {
              setAction('');
              setActor('');
            }}
          >
            Сбросить
          </button>
        )}
      </div>

      <Panel className="overflow-hidden">
        {loading || data === null ? (
          <Skeleton rows={8} />
        ) : data.entries.length === 0 ? (
          <EmptyState
            title="Записей нет"
            description={
              filtered
                ? 'Под выбранные условия ничего не подошло.'
                : 'Действия появятся здесь по мере работы бота и админки.'
            }
          />
        ) : (
          <ul>
            {data.entries.map((entry) => (
              <li
                key={entry.id}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-[var(--surface-hover)]"
                style={{ borderTop: '1px solid var(--border-subtle)' }}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <Badge tone={entry.source === 'web' ? 'accent' : entry.source === 'system' ? 'neutral' : 'positive'}>
                    {SOURCE_LABEL[entry.source] ?? entry.source}
                  </Badge>
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{ACTION_LABEL[entry.action] ?? entry.action}</div>
                    <div className="truncate text-xs" style={{ color: 'var(--text-muted)' }}>
                      {entry.actorId === null
                        ? 'автоматически'
                        : `исполнитель ${entry.actorId}${nameOf(entry.actorId) === null ? '' : ` · ${String(nameOf(entry.actorId))}`}`}
                      {entry.eventId === null ? '' : ` · набор #${String(entry.eventId)}`}
                      {entry.targetId === null
                        ? ''
                        : ` · игрок ${entry.targetId}${nameOf(entry.targetId) === null ? '' : ` · ${String(nameOf(entry.targetId))}`}`}
                    </div>
                  </div>
                </div>
                <div className="shrink-0 text-xs" style={{ color: 'var(--text-muted)' }}>
                  {formatDateTime(entry.createdAt, timezone)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </>
  );
}
